/**
 * useShellVox - the vox (v8.363.4): who is here, who they act as, and the chat.
 *
 * `voxMe` is this seat - null outside a session, which is what switches the
 * whole vox (and the handouts built on it) off. A private line for you opens
 * the curtain and a toast names who spoke (v8.363.5). The open shared log
 * shows EVERY line addressed to you, whichever chip is active, so
 * `markReadAll` reads everything.
 *
 * Returns `{ actingBy, voxPeople, voxMe, channelsApi, chatContextValue }`.
 * Extracted from DeathWatchSimulator.
 */
import { useMemo } from 'react';
import * as multiplayer from '../multiplayer';
import { actingByFrom } from '../engine/roster/actingBy';
import { liveryByCombatant } from '../engine/roster/team';
import { peopleFrom } from '../engine/chat';
import { toMs } from '../utils/timestamps';
import useChat from './useChat';
import useChannels from './useChannels';
import useUnread from './useUnread';
import useChatSignals from './useChatSignals';
import { useStableApi } from './useStableCallback';
import useStableValue from './useStableValue';

/** This seat on the vox, or null outside a session. */
export function voxSeat(sessionId, user, playerId, isGm) {
  if (!sessionId || !user?.uid || !playerId) return null;
  return { uid: user.uid, seat: playerId, name: user.sessionName || user.displayName || 'Unknown', isGm };
}

/** The newest message's timestamp - how far "read" reaches. */
const newestTs = (messages) => messages.reduce((t, m) => Math.max(t, m.ts), 0);

/** The chat context: the chat API plus who is here, and the two ways to mark it read. */
export function chatContextOf(voxMe, { chatApi, unreadApi, channelsApi, ...rest }) {
  if (!voxMe) return null;
  return {
    ...chatApi,
    me: voxMe.uid,
    isGm: rest.isGm,
    people: rest.voxPeople,
    channels: channelsApi.channels,
    channelsApi,
    actor: rest.voxActor,
    actingBy: rest.actingBy,
    unread: unreadApi.unread,
    markRead: (conv) => unreadApi.markRead(conv, newestTs(chatApi.messages)),
    markReadAll: () => {
      const newest = newestTs(chatApi.messages);
      for (const conv of Object.keys(unreadApi.unread.byConv)) unreadApi.markRead(conv, newest);
    },
  };
}

/** Who is on the vox: the seats, who each acts as, this seat, the GM and my actor. */
function useVoxPeople({ connectedPlayers, roster, sessionId, user, playerId, isGm, actor }) {
  // Who acts as whom, and who is on the vox, the same while they read the same (plan shell-contexts, A4): rebuilt
  // with every heartbeat and every roster write, they gave the chat and handouts contexts a new value each time.
  const actingBy = useStableValue(useMemo(() => actingByFrom(connectedPlayers, toMs), [connectedPlayers]));
  const voxPeople = useStableValue(
    useMemo(
      () => peopleFrom(connectedPlayers, actingBy, (id) => roster.find((c) => c.id === id)?.name ?? null),
      [connectedPlayers, actingBy, roster],
    ),
  );
  const { uid, sessionName, displayName } = user ?? {};
  const voxMe = useMemo(
    () => voxSeat(sessionId, { uid, sessionName, displayName }, playerId, isGm),
    [sessionId, uid, sessionName, displayName, playerId, isGm],
  );
  const voxGmUid = useMemo(() => voxPeople.find((p) => p.isGm)?.uid ?? null, [voxPeople]);
  const voxActor = useStableValue(
    useMemo(
      () => (actor ? { name: actor.name, livery: liveryByCombatant(roster)[actor.id] ?? null } : null),
      [actor, roster],
    ),
  );
  return { actingBy, voxPeople, voxMe, voxGmUid, voxActor };
}

/**
 * @param {object} args - connectedPlayers, roster, sessionId, user,
 *   playerId, isGm, actor, addRollNarrative, isLocked, chatToasts,
 *   summonLog, pushToast.
 */
export default function useShellVox(args) {
  const { sessionId, isGm } = args;
  const { actingBy, voxPeople, voxMe, voxGmUid, voxActor } = useVoxPeople(args);
  const me = voxMe?.uid ?? '';
  // Data and actions, each the same object until its data changes (useStableApi): every function here is an action.
  const channelsApi = useStableApi(useChannels({ sessionId, me, mp: multiplayer }));
  const chatApi = useStableApi(
    useChat({
      sessionId,
      me: voxMe,
      gmUid: voxGmUid,
      people: voxPeople,
      channels: channelsApi.channels,
      actor: voxActor,
      addRollNarrative: args.addRollNarrative,
      isLocked: args.isLocked,
      mp: multiplayer,
    }),
  );
  const unreadApi = useStableApi(useUnread(sessionId, chatApi.messages, me));
  const { chatToasts, summonLog, pushToast } = args;
  useChatSignals({
    messages: chatApi.messages,
    me: voxMe?.uid ?? null,
    toasts: chatToasts,
    summon: summonLog,
    pushToast,
  });
  const chatContextValue = useMemo(
    () => chatContextOf(voxMe, { chatApi, unreadApi, channelsApi, isGm, voxPeople, voxActor, actingBy }),
    [voxMe, chatApi, isGm, voxPeople, channelsApi, voxActor, actingBy, unreadApi],
  );
  return { actingBy, voxPeople, voxMe, channelsApi, chatContextValue };
}
